import type { BookingState } from './types';

const LISBON_TIME_ZONE = 'Europe/Lisbon';

/** Offset (in minutes, UTC+N) of `timeZone` at the instant `date` represents. */
function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUtc - date.getTime()) / 60_000;
}

/**
 * RFC3339 pickup time for a scheduled ride, or `undefined` for an ASAP ride.
 *
 * The wheel picker's date/time is always Europe/Lisbon wall-clock (the Range
 * Rover service only operates in Lisbon), so it's resolved against that
 * timezone explicitly here — not the device's — so a traveling user (or a
 * device clock set to another zone) still books the Lisbon time they picked.
 * The offset is Lisbon's actual one for the given date (handles DST), so the
 * result stays a valid absolute instant for any timezone-aware consumer.
 */
export function getPickupTimeIso(booking: BookingState): string | undefined {
  if (!booking.date || !booking.time) return undefined;

  const year = booking.date.getFullYear();
  const month = booking.date.getMonth() + 1;
  const day = booking.date.getDate();
  const [hour, minute] = booking.time.split(':').map(Number);

  // First guess: treat the picked wall-clock values as UTC, then read Lisbon's
  // actual offset at that instant.
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offsetMinutes = getTimeZoneOffsetMinutes(new Date(utcGuess), LISBON_TIME_ZONE);

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const offsetHours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
  const offsetMins = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00${sign}${offsetHours}:${offsetMins}`;
}
